<template>
  <div id="app" :class="{ 'flex h-full': $route.meta.guest }">
    <template v-if="!loading && dashboardConfig">
      <template v-if="!$route.meta.guest">
        <dashboard-sidebar />

        <div class="content-side" :class="{ '-fullscreen': !sidebarVisible }">
          <dashboard-nav-bar />
          <dashboard-page-area>
            <router-view />
          </dashboard-page-area>
        </div>

        <dashboard-overlay-out-sidebar />
      </template>
      <template v-else>
        <authentication-page-side>
          <router-view />
        </authentication-page-side>
        <authentication-description-side />
      </template>
    </template>

    <loader v-if="loading" opacity="1" />

    <div class="notification-prompt" v-if="user.id !== null && showNotification">
      <span class="close" @click="showNotification = false">
        <ion-icon name="close-outline"></ion-icon>
      </span>
      <ion-icon name="notifications-outline" style="top: 5px"></ion-icon>
      <p> Deseja ser notificado quando suas consultas ficam prontas? Permita as notificações do navegador.</p>
      <button type="button" class="btn btn-primary -sm" @click="requestPermission">Permitir</button>
    </div>

    <notifications group="app" position="top right" classes="app-notification" :duration="4000" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import AuthenticationPageSide from '@/auth/components/layout/PageSide.vue';
import AuthenticationDescriptionSide from '@/auth/components/layout/DescriptionSide.vue';
import DashboardSidebar from '@/dashboard/components/layout/Sidebar.vue';
import DashboardNavBar from '@/dashboard/components/layout/NavBar.vue';
import DashboardPageArea from '@/dashboard/components/layout/PageArea.vue';
import DashboardOverlayOutSidebar from '@/dashboard/components/layout/OverlayOutSidebar.vue';
import Loader from '@/support/components/Loader.vue';
import { initializeApp } from 'firebase/app';
import { getToken } from 'firebase/messaging';
import { getMessaging } from 'firebase/messaging/sw';

export default {
  data() {
    return {
      showNotification: true,
    };
  },

  methods: {
    /**
     * Load the authenticated user.
     *
     * @returns {Promise<void>}
     */
    async loadUser() {
      try {
        this.$store.commit('applicationWasLoading');
        await this.$store.dispatch('auth/loadUser');
        this.$store.commit('applicationWasNotLoading');
      } catch {
        this.$store.commit('applicationWasNotLoading');
      }
    },

    async requestPermission() {
      console.log('Requesting permission...');
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        this.showNotification = false;
        await this.initializeFirebase();
      } else {
        console.log('Notification permission NOT granted.');
        this.showNotification = true;
      }
    },

    async initializeFirebase() {
      if (this.user.id === null) {
        return;
      }

      const firebaseConfig = {
        apiKey: 'AIzaSyBedb55QMGHxzc9QPnCGT9RJppS3HsTBrI',
        authDomain: 'plexi-334315.firebaseapp.com',
        projectId: 'plexi-334315',
        storageBucket: 'plexi-334315.appspot.com',
        messagingSenderId: '539001417205',
        appId: '1:539001417205:web:0ed62b79508b24cd1744bc',
      };

      try {
        const firebaseApp = initializeApp(firebaseConfig);
        const messaging = getMessaging(firebaseApp);

        const currentToken = await getToken(messaging, { vapidKey: 'BEOpIqww8UVfktYpvpxcwpoeu-9X1F4kwk1hZuq7DpA98pQF2FiYYx9-1MB9L8yRs8RBSFc7Gwgwsq96HUw2nA8' });

        if (currentToken) {
          await this.$store.dispatch('auth/registerFirebaseCloudMessagingToken', currentToken);
        }
      } catch (err) {
        console.log('An error occurred while retrieving token. ', err);
      }
    },

    isNotificationIsPermitted() {
      if (Notification.permission === 'granted') {
        this.showNotification = false;
        return true;
      }

      return false;
    },
  },

  computed: {
    ...mapState({
      loading: 'applicationLoading',
    }),
    ...mapState('dashboard', [
      'sidebarVisible',
      'dashboardConfig',
    ]),
    ...mapState('auth', [
      'accessToken',
      'user',
    ]),
  },

  watch: {
    sidebarVisible(sidebarVisible) {
      if (sidebarVisible) {
        document.body.classList.add('sidebar-is-visible');
        return;
      }

      document.body.classList.remove('sidebar-is-visible');
    },

    /**
     *
     * @param to
     * @param from
     * @returns {Promise<void>}
     */
    async $route(to) {
      if (to.meta.guest === true || to.name === 'Auth-Logout') {
        this.showNotification = false;
      } else {
        await this.requestPermission();
      }
    },
  },

  async mounted() {
    if (this.accessToken && this.$route.name !== 'Auth-Logout') {
      await this.loadUser();

      if (this.isNotificationIsPermitted()) {
        await this.initializeFirebase();
      }
    } else {
      this.$store.commit('applicationWasNotLoading');
    }
  },

  components: {
    AuthenticationPageSide,
    AuthenticationDescriptionSide,
    DashboardSidebar,
    DashboardNavBar,
    DashboardPageArea,
    DashboardOverlayOutSidebar,
    Loader,
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@200;300;400;500;600;700&family=Roboto+Slab:wght@700&display=swap');

html,
body {
  height: 100%;
}

body {
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;

  &.sidebar-is-visible {
    overflow-y: hidden;

    @media (min-width: 768px) {
      overflow-y: auto;
    }
  }
}

#app {
  .content-side {
    background-color: #f5f6fa;
    min-height: 100vh;
    transition-duration: .4s;
    transition-timing-function: cubic-bezier(.25, .8, .25, 1);
    transition-property: visibility, margin-left, margin-right, transform, width, max-width, min-width;

    @screen md {
      margin-left: 280px;
    }

    &.-fullscreen {
      margin-left: 0;
    }
  }

  .loader {
    z-index: 11;
  }

  .notification-prompt {
    position: fixed;
    bottom: 67px;
    right: 13px;
    background-color: #fff;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0 0 10px rgba(0, 0, 0, .2);
    width: 400px;

    >.close {
      position: absolute;
      top: 10px;
      right: 10px;
      cursor: pointer;
    }

    >p {
      margin-bottom: 14px;
      font-size: 90%;
    }
  }
}
</style>
